import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  assertLogFigures,
  createProject,
  getEngagementCategories,
  previewPortfolio,
  type EngagementCategoryOption,
  type PortfolioPreview,
  type PortfolioProposal,
} from '../api.js';
import { useChrome } from '../chrome.js';
import { BackLink } from '../components/BackLink.js';
import { useSession } from '../useSession.js';
import { canCreateProject, canMigrateNumberedProject } from '../roles.js';

/**
 * PORTFOLIO IMPORT — the GGP Project Tracking Log, confirmed one record at a time.
 *
 * GGP's whole book of work lives in one spreadsheet on the X Drive: sixteen projects, $146.9M of
 * construction value, measured 2026-08-27, while the platform held one of them. `pnpm
 * portfolio:export --rows` gets the sheet off the drive; `POST /portfolio/preview` maps it; this page
 * is where a person decides which of the proposals become records.
 *
 * ## 🔴 SIXTEEN CONFIRMATIONS, NOT ONE IMPORT BUTTON
 *
 * Canon R-a: _"Project creation is human-initiated through the PEE only; no auto-create."_ There is no
 * bulk create endpoint and there should not be — this page calls `POST /projects` once per ticked row,
 * the same door a person uses to open one record by hand. Each carries its own attributed intake
 * Decision Record, so "who took this on?" stays a question with an answer for every one of the sixteen.
 *
 * ## ⚠️ THE CATEGORY IS ASKED PER ROW, AND IT IS PERMANENT
 *
 * The engagement category decides the trailing token of the job number, which is minted at Established
 * and never reissued. Confirming sixteen rows with the box untouched stamps sixteen records `Proj`
 * forever, so the choice is offered on every row and its consequence is printed beside it.
 *
 * ⚠️ AND IT IS NOT DERIVED FROM THE LOG'S BUSINESS LINE. The sheet numbers projects `YY.LINE.SEQ` —
 * 100 development, 200 vertically integrated, 300 GC third-party — and those do not map onto Chad's
 * four categories: `200 vertically integrated` matches none of them. Guessing would be minting a
 * numbering scheme in the UI, which `job-number.ts` reserves as his ruling.
 */
export function PortfolioImport() {
  const session = useSession();
  const Chrome = useChrome();

  const [paste, setPaste] = useState('');
  const [preview, setPreview] = useState<PortfolioPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Ticked rows, keyed on the log's number — the only stable identifier a proposal has. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /** Chosen category per row. Absent ⇒ "Something else" ⇒ the server's documented `Proj` fallback. */
  const [categoryFor, setCategoryFor] = useState<Record<string, string>>({});
  /**
   * The existing project number, per row, for the rows that need one.
   *
   * ⚠️ ONLY EVER TYPED BY A PERSON. The log's own number is not a project number — `planPortfolioIngest`
   * warns exactly that on every proposal, and `POST /portfolio/preview` strips `projectCode` from the
   * wire so the sheet cannot confer one. This is the reader supplying a number they already know, which
   * is the migrate branch `planIntake` describes; it is not the import inventing one.
   */
  const [numberFor, setNumberFor] = useState<Record<string, string>>({});
  /** Which row's figures are in flight, so one press cannot start two. */
  const [figuresBusy, setFiguresBusy] = useState<string | null>(null);
  /** What happened per row, in the server's own terms. */
  const [figureOutcomes, setFigureOutcomes] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<EngagementCategoryOption[]>([]);
  const [fallbackTypeCode, setFallbackTypeCode] = useState('Proj');

  /** Per-row outcome after confirming, so a partial run says exactly which rows landed. */
  const [outcomes, setOutcomes] = useState<Record<string, string>>({});

  useEffect(() => {
    let live = true;
    void getEngagementCategories().then((res) => {
      if (!live) return;
      setCategories(res.categories);
      setFallbackTypeCode(res.fallbackTypeCode);
    });
    return () => {
      live = false;
    };
  }, []);

  if (session.status !== 'authed') return <div className="app__loading">Loading…</div>;
  // Same reasoning as the intake page: hiding the link is worthless, because this is a URL. A viewer
  // who arrives by bookmark goes to the list they actually came to see.
  if (!canCreateProject(session.claims)) return <Navigate to="/projects" replace />;

  async function handlePreview() {
    setBusy(true);
    setPreviewError(null);
    setPreview(null);
    setOutcomes({});
    let rows: unknown[];
    try {
      const parsed = JSON.parse(paste) as { rows?: unknown[] } | unknown[];
      // `--rows` emits `{ source, rows }`; a bare array is accepted too, because someone will paste
      // just the array and being strict about the wrapper helps nobody.
      rows = Array.isArray(parsed) ? parsed : (parsed.rows ?? []);
    } catch {
      setBusy(false);
      setPreviewError(
        'That is not valid JSON. Paste the output of `pnpm portfolio:export --rows`.',
      );
      return;
    }
    if (!Array.isArray(rows) || rows.length === 0) {
      setBusy(false);
      setPreviewError('No rows in that paste. Expected `{ "rows": [ … ] }`.');
      return;
    }
    const res = await previewPortfolio(rows);
    setBusy(false);
    if (res.status !== 'ok') {
      setPreviewError(res.reason);
      return;
    }
    setPreview(res.preview);
    // ⚠️ NOTHING IS TICKED BY DEFAULT, and rows already imported could not be ticked anyway. A page
    // that pre-selects sixteen permanent decisions has made them for the reader.
    setSelected(new Set());
  }

  /*
    ⚠️ AN ACTIVE ROW IS PROPOSED AS A NUMBERED PROJECT, AND A NUMBERED PROJECT CARRIES ITS NUMBER.

    `classFor` proposes `ggp_project` for a row the log marks Active; `planIntake` then refuses a
    `ggp_project` that arrives without a number (`project-needs-number`, R-e). This page used to send
    `projectCode: null` for every row, so an Active row could never be imported at all — it was ticked,
    confirmed, and came back refused, every time. One row in today's log is Active, and it is the one
    project the platform already held.

    So the number is asked for, exactly as the intake form asks for it, and the row cannot be ticked
    until it is given. Refusing at confirm time what could be seen at preview time is the failure the
    "already imported" check on this same page exists to avoid.
  */
  const needsNumber = (p: PortfolioProposal): boolean => p.recordClass === 'ggp_project';
  const numberGiven = (p: PortfolioProposal): string | null =>
    (numberFor[p.sourceRef] ?? '').trim() || null;
  /*
    ⚠️ AND ONLY AN APPROVER MAY MIGRATE ONE. `POST /projects` 403s a numbered create from a `member`,
    who can reach this page perfectly well — so without this a member would type a number, tick, confirm
    and collect a 403 per row. That is the same "offered an action you cannot take" failure, moved one
    step later. The server is still the control; this stops the page advertising against it.
  */
  const mayMigrate = canMigrateNumberedProject(session.claims);
  const readyToImport = (p: PortfolioProposal): boolean =>
    !needsNumber(p) || (mayMigrate && numberGiven(p) !== null);

  /**
   * Put one already-imported row's stated figures onto the record it became.
   *
   * ⚠️ ADDRESSED BY `code ?? id`. A pursuit carries no project number (canon R-e), and `/projects/:code`
   * resolves a uuid too — addressing by code alone would make this button dead on exactly the class of
   * record the import creates most of.
   */
  async function recordFigures(p: PortfolioProposal) {
    const existing = p.existing;
    if (!existing || figuresBusy !== null) return;
    setFiguresBusy(p.sourceRef);
    const res = await assertLogFigures({
      ref: existing.code ?? existing.id,
      sourceRef: p.sourceRef,
      dateUpdated: p.stated.dateUpdated,
      stated: {
        unitQty: p.stated.units,
        buildingSf: p.stated.buildingSf,
        developerBudget: p.stated.developerBudget,
        fee: p.stated.fee,
        estimate: p.stated.estimate,
        projectStatus: p.stated.projectStatus ?? null,
        designStatus: p.stated.designStatus,
      },
    });
    setFiguresBusy(null);
    setFigureOutcomes((prev) => ({
      ...prev,
      [p.sourceRef]:
        res.status === 'ok'
          ? res.asserted === 0
            ? `Nothing to record — the log has not moved (${String(res.unchanged)} figures unchanged).`
            : `Recorded ${String(res.asserted)} figure${res.asserted === 1 ? '' : 's'}: ${res.assertedKeys.join(', ')}.`
          : res.reason,
    }));
  }

  async function handleConfirm() {
    if (!preview) return;
    setBusy(true);
    const results: Record<string, string> = {};
    /*
      ⚠️ SEQUENTIAL, NOT `Promise.all`. Each intake allocates a job number by an upsert inside its own
      transaction, and firing sixteen at once would contend on the same sequence row for no gain on a
      one-off import. Sequential also means a failure stops with a readable partial result rather than
      an interleaved one.
    */
    for (const p of preview.proposals) {
      if (!selected.has(p.sourceRef) || p.existing) continue;
      /*
        ⚠️ NOT MERELY DEFENSIVE — this state is reachable. Ticking a numbered row requires a number, but
        nothing stops the reader clearing the field afterwards, and the tick would survive. Sending the
        row anyway would spend a confirm on a refusal the page already knew about. `onChange` unticks
        on clear so the UI stays truthful; this is the backstop that makes the loop total either way.
      */
      if (needsNumber(p) && numberGiven(p) === null) {
        results[p.sourceRef] =
          'not imported — a GGP project carries its number, and this row has none';
        setOutcomes({ ...results });
        continue;
      }
      const res = await createProject({
        recordClass: p.recordClass,
        projectName: p.projectName,
        /*
          The number the reader supplied, and only for a class that may carry one. A pursuit sends null
          — `planIntake` refuses `number-before-promotion` otherwise, which is R-e holding on the way in.
        */
        projectCode: needsNumber(p) ? numberGiven(p) : null,
        sponsorName: p.sponsorName,
        engagementCategory: categoryFor[p.sourceRef] || null,
        sourceRef: p.sourceRef,
      });
      results[p.sourceRef] =
        res.status === 'created'
          ? `created${res.project.code ? ` · ${res.project.code}` : ''}`
          : `${res.status} — ${res.reason}`;
      setOutcomes({ ...results });
    }
    setBusy(false);
  }

  function toggle(ref: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref);
      else next.add(ref);
      return next;
    });
  }

  const selectable = (preview?.proposals ?? []).filter((p) => !p.existing);
  /** Importable rows still missing the number they cannot be created without. */
  const awaitingNumber = selectable.filter((p) => !readyToImport(p)).length;
  const tokenFor = (p: PortfolioProposal) =>
    categories.find((c) => c.value === categoryFor[p.sourceRef])?.typeCode ?? fallbackTypeCode;

  return (
    <div className="app">
      {Chrome !== null && <Chrome claims={session.claims} />}
      <main className="app__main">
        <div className="app__crumbs">
          <BackLink fallback="/projects" label="All projects" />
        </div>

        <div className="app__intro">
          <h1>Import the tracking log</h1>
          <p className="sub">
            Run <code>pnpm portfolio:export --rows</code> and paste the output below. Nothing is
            created until you tick a row and confirm — each one opens a record through the same door
            you would use by hand, and carries your name on its intake.
          </p>
        </div>

        <form className="df" onSubmit={(e) => e.preventDefault()}>
          <label className="df__field df__field--full">
            <span className="df__label">Tracking log rows (JSON)</span>
            <span className="df__inputwrap">
              <textarea
                rows={6}
                value={paste}
                onChange={(e) => setPaste(e.target.value)}
                placeholder={'{ "source": "…GGP Project Tracking Log.xlsx", "rows": [ … ] }'}
              />
            </span>
            <span className="df__hint">
              The rows as read from the sheet. The mapping runs on the server, so what you see below
              is what the platform would actually create.
            </span>
          </label>
          <button
            type="button"
            disabled={busy || paste.trim() === ''}
            onClick={() => void handlePreview()}
          >
            {busy ? 'Reading…' : 'Preview'}
          </button>
        </form>

        {previewError !== null && (
          <p className="df__error" role="alert">
            {previewError}
          </p>
        )}

        {preview !== null && (
          <>
            <div className="app__intro">
              <h2>
                {preview.totals.proposed} project{preview.totals.proposed === 1 ? '' : 's'} proposed
              </h2>
              <p className="sub">
                {preview.totals.units.toLocaleString()} units ·{' '}
                {preview.totals.buildingSf.toLocaleString()} building SF · construction value{' '}
                {money(preview.totals.estimate)} across {preview.totals.withEstimate} of{' '}
                {preview.totals.proposed}
                {/* A total that hides its denominator misleads — the planner carries the count for
                    exactly this sentence. */}
              </p>
            </div>

            <ul className="projlist">
              {preview.proposals.map((p) => (
                <li className="card" key={p.sourceRef}>
                  <label className="df__check">
                    <input
                      type="checkbox"
                      checked={selected.has(p.sourceRef)}
                      // A row that cannot be created is not offered. See the `needsNumber` note above:
                      // the alternative is a tick that always comes back refused.
                      disabled={p.existing !== null || busy || !readyToImport(p)}
                      onChange={() => toggle(p.sourceRef)}
                    />
                    <span className="df__checkbody">
                      <strong>{p.projectName}</strong>
                      <span className="df__hint">
                        {p.sourceRef} ·{' '}
                        {p.recordClass === 'ggp_project' ? 'GGP project' : 'pursuit'}
                        {p.city !== null && ` · ${p.city}${p.state !== null ? `, ${p.state}` : ''}`}
                        {p.sponsorName !== null && ` · ${p.sponsorName}`}
                      </span>
                      {/*
                        WHAT THE LOG SAYS THIS PROJECT IS. Somebody deciding whether to take a project
                        on needs its size and money, and the CLI prints them — a confirm page that
                        showed only the name would be asking for a permanent decision on less
                        information than the terminal gives.

                        Only the figures the sheet actually states: a blank cell renders as nothing
                        rather than as a zero, because "$0 fee" and "no fee stated" are different
                        claims and the row is hand-maintained.
                      */}
                      {statedLine(p) !== '' && <span className="df__hint">{statedLine(p)}</span>}
                    </span>
                  </label>

                  {p.existing !== null ? (
                    /* Already imported. Shown rather than hidden: a row that vanished would read as a
                       row the log never had. */
                    <div className="df__hint">
                      <p>
                        Already imported as{' '}
                        <strong>{p.existing.name ?? p.existing.code ?? 'a record'}</strong> —
                        importing it again would create a second record, so it is refused.
                      </p>
                      {/*
                        🔴 THE ROW IS NO LONGER A DEAD END. The sheet is hand-maintained and its
                        numbers move — an estimate is revised, a project goes Pending → Active — and
                        until this existed the only thing an already-imported row could do was refuse.
                        The figures now land on the record as facts (Chad's 2026-08-13 ruling), where
                        the record already renders them.

                        ⚠️ It asserts what CHANGED. `record_facts` is append-only, so re-pressing this
                        on an unmoved log writes nothing and says so.
                      */}
                      <button
                        type="button"
                        className="docs__submit"
                        disabled={busy || figuresBusy === p.sourceRef}
                        onClick={() => void recordFigures(p)}
                      >
                        {figuresBusy === p.sourceRef
                          ? 'Recording…'
                          : 'Record the log’s current figures'}
                      </button>
                      {figureOutcomes[p.sourceRef] !== undefined && (
                        <p>
                          <strong>{figureOutcomes[p.sourceRef]}</strong>
                        </p>
                      )}
                    </div>
                  ) : (
                    <label className="df__field">
                      <span className="df__label">Engagement category</span>
                      <span className="df__inputwrap">
                        <select
                          value={categoryFor[p.sourceRef] ?? ''}
                          disabled={busy}
                          onChange={(e) =>
                            setCategoryFor((prev) => ({ ...prev, [p.sourceRef]: e.target.value }))
                          }
                        >
                          <option value="">Something else</option>
                          {categories.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.value}
                            </option>
                          ))}
                        </select>
                      </span>
                      <span className="df__hint">
                        Numbered{' '}
                        <strong>{`${new Date().getUTCFullYear()}-####-${tokenFor(p)}`}</strong> —
                        minted now and never reissued.
                      </span>
                    </label>
                  )}

                  {p.existing === null && needsNumber(p) && (
                    /*
                      ⚠️ THE JOB NUMBER ABOVE IS NOT THIS NUMBER, and the two sit adjacent so the
                      difference has to be said. The job number is minted here from the category and is
                      the permanent correlation key (canon D1); the project number is the identity this
                      project's existing documents are already stamped with, and only a person knows it.
                    */
                    <label className="df__field">
                      <span className="df__label">Existing GGP project number</span>
                      <span className="df__inputwrap">
                        <input
                          type="text"
                          maxLength={64}
                          value={numberFor[p.sourceRef] ?? ''}
                          disabled={busy || !mayMigrate}
                          placeholder="2025-014"
                          onChange={(e) => {
                            const next = e.target.value;
                            setNumberFor((prev) => ({ ...prev, [p.sourceRef]: next }));
                            // Clearing the number unticks the row, so the page never shows a tick it
                            // would not act on. The confirm loop guards this too.
                            if (next.trim() === '') {
                              setSelected((prev) => {
                                if (!prev.has(p.sourceRef)) return prev;
                                const rest = new Set(prev);
                                rest.delete(p.sourceRef);
                                return rest;
                              });
                            }
                          }}
                        />
                      </span>
                      <span className="df__hint">
                        The log marks this project active, so it already carries a number and cannot
                        be imported without it (R-e). The log&rsquo;s own{' '}
                        <strong>{p.sourceRef}</strong> is not that number.
                        {!mayMigrate &&
                          ' Bringing in an already-numbered project is an owner or admin decision, so this row is not yours to import — ask one of them.'}
                      </span>
                    </label>
                  )}

                  {p.warnings.length > 0 && (
                    /* Never a blocker — a warning that stopped the import would make the whole log
                       un-importable over one bad cell. */
                    <ul className="df__hint plainlist">
                      {p.warnings.map((w) => (
                        <li key={w}>⚠️ {w}</li>
                      ))}
                    </ul>
                  )}

                  {outcomes[p.sourceRef] !== undefined && (
                    <p className="df__hint">
                      <strong>{outcomes[p.sourceRef]}</strong>
                    </p>
                  )}
                </li>
              ))}
            </ul>

            {preview.skipped.length > 0 && (
              <div className="app__intro">
                <h2>Not proposed</h2>
                <ul className="df__hint plainlist">
                  {preview.skipped.map((s) => (
                    <li key={`${String(s.rowIndex)}-${s.sourceRef ?? ''}`}>
                      row {s.rowIndex + 1}
                      {s.sourceRef !== null && ` (${s.sourceRef})`} — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              disabled={busy || selected.size === 0}
              onClick={() => void handleConfirm()}
            >
              {busy
                ? 'Opening records…'
                : `Open ${String(selected.size)} record${selected.size === 1 ? '' : 's'}`}
            </button>
            <p className="df__hint">
              {/*
                ⚠️ A ROW WAITING FOR A NUMBER IS NOT A ROW THAT ALREADY EXISTS. This sentence used to
                say the remainder already existed, which was the only reason a row could be untickable
                — it no longer is, and a footer that explains the wrong absence sends the reader looking
                for a record that was never created.
              */}
              {selectable.length} of {preview.proposals.length} can be imported; the rest already
              exist.
              {awaitingNumber > 0 &&
                ` ${String(awaitingNumber)} of those ${awaitingNumber === 1 ? 'is an active project waiting for its' : 'are active projects waiting for their'} existing project number.`}{' '}
              Each opens through the intake door and carries your name.
            </p>
          </>
        )}
      </main>
    </div>
  );
}

/** Matches the CLI's own rendering, so the two readings of one log agree on screen. */
function money(n: number): string {
  return n === 0 ? '—' : `$${(n / 1_000_000).toFixed(n >= 1_000_000 ? 2 : 3)}M`;
}

/**
 * The log's own figures for one proposal, as a single line.
 *
 * ⚠️ A NULL IS OMITTED, NEVER RENDERED AS ZERO. The sheet is hand-maintained and carries `N/A` and
 * `TBD` freely, so an unstated fee and a stated fee of nothing are different claims — and this line
 * sits beside a button that creates a permanent record.
 */
function statedLine(p: PortfolioProposal): string {
  const s = p.stated;
  const bits: string[] = [];
  if (s.units !== null) bits.push(`${s.units.toLocaleString()} units`);
  if (s.buildingSf !== null) bits.push(`${s.buildingSf.toLocaleString()} SF`);
  if (s.developerBudget !== null) bits.push(`budget ${money(s.developerBudget)}`);
  if (s.fee !== null) bits.push(`fee ${money(s.fee)}`);
  if (s.estimate !== null) bits.push(`estimate ${money(s.estimate)}`);
  if (s.projectType !== null) bits.push(s.projectType);
  if (s.designStatus !== null) bits.push(s.designStatus);
  if (s.dateUpdated !== null) bits.push(`updated ${s.dateUpdated}`);
  return bits.join(' · ');
}
